import React, { useState } from "react";
import { ServicePageHero, FeatureGrid, ProcessSteps, CTAStripe } from "@/components/sections/Shared";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES } from "@/lib/business";
import { Camera, Bell, Cloud, Smartphone, Video, LockKeyhole } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { serviceJsonLd } from "@/components/SEO";

const FEATURES = [
    { icon: Camera, title: "4K HD Cameras", description: "Crisp day, night and colour-night footage that actually identifies people and plates." },
    { icon: Smartphone, title: "Mobile Alerts", description: "Real-time motion and person detection notifications sent straight to your phone." },
    { icon: Cloud, title: "Cloud & Local Storage", description: "Flexible options — keep your footage private on an NVR, secure in the cloud, or both." },
    { icon: Bell, title: "Smart Detection", description: "AI-powered people, vehicle, and package detection cuts false alerts dramatically." },
    { icon: LockKeyhole, title: "Secure & Encrypted", description: "Encrypted streams, strong default settings, and professional setup you can trust." },
    { icon: Video, title: "Remote Viewing", description: "Watch live or review footage from anywhere — mobile, tablet, or desktop." },
];

const FAQS = [
    {
        q: "Can I view my cameras from my phone?",
        a: "Yes. Every system we install comes with a free mobile app — live view, playback, and push alerts from anywhere.",
    },
    {
        q: "Do I need internet for the cameras to record?",
        a: "No. We can configure local recording to an NVR so footage is saved even if your internet is down. Add cloud storage for extra peace of mind.",
    },
    {
        q: "How many cameras do I need?",
        a: "Most homes are well covered with 4-6 cameras (front, back, driveway, side yard). We recommend coverage based on your property at the quote stage.",
    },
    {
        q: "Do you provide maintenance and support?",
        a: "Yes. We offer annual maintenance plans and priority support — because a camera system is only useful when it's working.",
    },
];

export default function SecurityCameras() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Security Camera Installation in the GTA"
                description="4K HD security camera systems professionally installed with mobile alerts, cloud/local recording, and smart AI detection for homes and businesses."
                path="/security-cameras"
                keywords="security cameras GTA, CCTV installation Toronto, 4K camera system, smart camera installation, NVR cameras"
                jsonLd={serviceJsonLd(
                    "Security Camera Installation",
                    "Professional 4K HD security camera systems with mobile app, cloud and local storage, across the Greater Toronto Area.",
                )}
            />
            <ServicePageHero
                eyebrow="Security Cameras"
                title="Smart, HD cameras — professionally installed."
                subtitle="Keep a watchful eye on your home or business with modern, AI-enabled camera systems, installed cleanly and set up to work on day one."
                image={IMAGES.cameras}
                defaultQuoteService="Security Cameras"
                bullets={[
                    "4K HD day, night and colour-night recording",
                    "Mobile app with live view and instant alerts",
                    "Local NVR, cloud, or hybrid storage options",
                    "Professional cable management and clean install",
                ]}
            />

            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> What's included
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Everything you need for peace of mind.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <FeatureGrid items={FEATURES} />
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> From quote to live feed
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            A simple, three-step install.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                { title: "Walkthrough & plan", description: "We review your property, identify blind spots, and recommend the right camera count and placements." },
                                { title: "Professional install", description: "Clean cable runs, secure mounting, and thorough configuration — no dangling wires, no guesswork." },
                                { title: "Mobile setup & training", description: "We set up the app on your phone and walk you through everything before we leave." },
                            ]}
                        />
                    </div>
                </div>
            </section>

            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Camera systems, explained.
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem value={`cam-${i}`} key={f.q} className="border-b border-gray-200">
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CTAStripe
                heading="Watch over what matters — from anywhere."
                subheading="Free on-site walk-through and quote. No pressure, ever."
                ctaLabel="Get a Camera Quote"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultService="Security Cameras"
            />
        </>
    );
}
